/**
 * Choosing which folders an account may see.
 *
 * The arithmetic only, away from the list that draws it, because two of these
 * decide what somebody can open and one of them is easy to get subtly wrong.
 *
 * Access runs downhill — a grant on /Invoices carries to /Invoices/2026/June
 * and everything filed in either — so the set of folders somebody can reach is
 * not the set of boxes that were ticked. `coveredBy` is that difference, and
 * the picker shows it rather than leaving the Owner to work it out.
 */

export interface PickableFolder {
  readonly id: string;
  readonly name: string;
  /** Materialised path, e.g. "/Invoices/2026/June". */
  readonly path: string;
}

/** How deep a path sits, so the list can be indented like a tree. */
export function depthOf(path: string): number {
  return path.split('/').filter(Boolean).length - 1;
}

/**
 * Whether a folder is inside another.
 *
 * The separator matters: without it /Invoices would contain /Invoices Archive,
 * two folders whose names share an opening and nothing else. The same rule the
 * server applies when it works out what a grant reaches.
 */
export function isInside(path: string, ancestor: string): boolean {
  return path.startsWith(ancestor + '/');
}

/**
 * The folders reachable through a selection without being in it.
 *
 * Ticking /Invoices already grants /Invoices/2026, so the picker greys that
 * one out and says why instead of letting somebody tick it again and believe
 * it did something.
 */
export function coveredBy(
  selected: ReadonlySet<string>,
  folders: readonly PickableFolder[],
): Set<string> {
  const chosen = folders.filter((folder) => selected.has(folder.id));
  if (chosen.length === 0) return new Set();

  const covered = new Set<string>();
  for (const folder of folders) {
    if (selected.has(folder.id)) continue;
    if (chosen.some((parent) => isInside(folder.path, parent.path))) covered.add(folder.id);
  }

  return covered;
}

/**
 * The folders worth sending, with the redundant ones dropped.
 *
 * Ticking /Invoices and then /Invoices/2026 is one grant, not two — the second
 * is already reachable through the first. The server drops it as well when it
 * decides what to show at the top of the Drive, and sending it anyway would
 * leave a row behind that survives the parent being un-granted.
 */
export function withoutRedundant(
  selected: ReadonlySet<string>,
  folders: readonly PickableFolder[],
): string[] {
  const chosen = folders.filter((folder) => selected.has(folder.id));

  return chosen
    .filter(
      (folder) => !chosen.some((other) => other !== folder && isInside(folder.path, other.path)),
    )
    .map((folder) => folder.id);
}

/**
 * Whether a folder answers to what was typed in the search box.
 *
 * Matched against the whole path, not just the name, so "invoices 2026" finds
 * /Invoices/2026 — and so that typing a parent's name brings its children up
 * with it, which is how somebody looks for a folder they half remember.
 *
 * Every word has to appear somewhere, in any order: the office writes
 * "ASFAND SALARY" and "SALARY ASFAND" for the same folder often enough that
 * requiring the order would hide it.
 */
export function matches(folder: PickableFolder, term: string): boolean {
  const words = term.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return true;

  const haystack = folder.path.toLowerCase();
  return words.every((word) => haystack.includes(word));
}

/**
 * The list to draw: what matched, plus the ancestors that hold them.
 *
 * A search that returned only the matches would show /Invoices/2026 as a
 * top-level row with no sign of where it lives, and two folders called "2026"
 * would be indistinguishable. Keeping the ancestors keeps the shape of the
 * tree, and they are marked so the list can draw them as structure rather than
 * as results.
 */
export function visibleFolders(
  folders: readonly PickableFolder[],
  term: string,
): Array<PickableFolder & { readonly matched: boolean }> {
  if (term.trim() === '') return folders.map((folder) => ({ ...folder, matched: true }));

  const hits = folders.filter((folder) => matches(folder, term));
  const keep = new Set(hits.map((folder) => folder.id));

  for (const hit of hits) {
    for (const folder of folders) {
      if (isInside(hit.path, folder.path)) keep.add(folder.id);
    }
  }

  return folders
    .filter((folder) => keep.has(folder.id))
    .map((folder) => ({ ...folder, matched: keep.has(folder.id) && matches(folder, term) }));
}

// ---------------------------------------------------------------------------
// Items granted on their own
// ---------------------------------------------------------------------------

/** The three things that live in a folder, as the grant rows name them. */
export type ItemKind = 'FILE' | 'DOCUMENT' | 'SPREADSHEET';

export interface PickableItem {
  readonly kind: ItemKind;
  readonly id: string;
  readonly name: string;
  /** The folder it lives in, or "/" for the Drive root. */
  readonly path: string;
}

/**
 * One key for one item.
 *
 * Kind and id together, because the three tables have their own id spaces: two
 * rows sharing a uuid in different tables are two different things, and a
 * selection keyed on the id alone would quietly hold one of them.
 */
export function itemKey(item: { readonly kind: ItemKind; readonly id: string }): string {
  return `${item.kind}:${item.id}`;
}

/** What each kind is called on screen. */
export const ITEM_LABEL: Record<ItemKind, string> = {
  FILE: 'File',
  SPREADSHEET: 'Sheet',
  DOCUMENT: 'Document',
};

/**
 * How a selection reads once it is made.
 *
 * Spelled out rather than given as one number, because "4 things" hides the
 * distinction the Owner just spent a minute making — a folder carries
 * everything inside it and an item carries only itself.
 */
export function describeSelection(folders: number, items: number, covered: number): string {
  if (folders === 0 && items === 0)
    return 'Nothing chosen — this account will sign in to an empty Drive.';

  const parts: string[] = [];
  if (folders > 0) {
    parts.push(`${folders} folder${folders === 1 ? '' : 's'}`);
    if (covered > 0) parts.push(`${covered} more inside them`);
  }
  if (items > 0) parts.push(`${items} single item${items === 1 ? '' : 's'}`);

  return `${parts.join(', ')} chosen`;
}
